'use client';

import Link from 'next/link';
import { Search, ArrowLeft, Scale, Calculator, Building, GraduationCap } from 'lucide-react';
import { usePathname } from 'next/navigation';
import { getLocaleFromPathname, toFrenchProfessionSlug } from '@/lib/i18n';

export default function NotFound() {
  const pathname = usePathname();
  const locale = getLocaleFromPathname(pathname || '');

  const localizeLink = (slug: string) => {
    const profSlug = locale === 'fr' ? toFrenchProfessionSlug(slug) : slug;
    return `/${locale}/ai-for-${profSlug}`;
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col items-center justify-center px-4">
      <div className="max-w-lg w-full text-center">

        {/* 404 Number */}
        <div className="relative mb-8">
          <p className="text-[8rem] font-bold text-primary/10 leading-none select-none">
            404
          </p>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="h-16 w-16 rounded-2xl bg-primary/10 flex items-center justify-center">
              <Search className="h-8 w-8 text-primary" />
            </div>
          </div>
        </div>

        {/* Message */}
        <h1 className="text-3xl font-bold text-foreground mb-4">
          Page Not Found
        </h1>
        <p className="text-muted-foreground text-lg mb-8 leading-relaxed">
          This page does not exist or has been moved. Try browsing our AI tool directories below.
        </p>

        {/* Quick Links */}
        <div className="grid grid-cols-2 gap-3 mb-10">
          <Link
            href={localizeLink('legal')}
            className="flex items-center gap-3 p-4 rounded-xl border border-border bg-card hover:border-indigo-500/50 hover:text-indigo-400 transition-all text-sm font-semibold text-foreground"
          >
            <Scale className="h-5 w-5 text-indigo-500 shrink-0" />
            Legal AI Tools
          </Link>
          <Link
            href={localizeLink('real-estate')}
            className="flex items-center gap-3 p-4 rounded-xl border border-border bg-card hover:border-indigo-500/50 hover:text-indigo-400 transition-all text-sm font-semibold text-foreground"
          >
            <Building className="h-5 w-5 text-indigo-500 shrink-0" />
            Real Estate AI
          </Link>
          <Link
            href={localizeLink('finance')}
            className="flex items-center gap-3 p-4 rounded-xl border border-border bg-card hover:border-indigo-500/50 hover:text-indigo-400 transition-all text-sm font-semibold text-foreground"
          >
            <Calculator className="h-5 w-5 text-indigo-500 shrink-0" />
            Finance AI Tools
          </Link>
          <Link
            href={localizeLink('education')}
            className="flex items-center gap-3 p-4 rounded-xl border border-border bg-card hover:border-indigo-500/50 hover:text-indigo-400 transition-all text-sm font-semibold text-foreground"
          >
            <GraduationCap className="h-5 w-5 text-indigo-500 shrink-0" />
            Education AI
          </Link>
        </div>

        {/* Back Home */}
        <Link
          href={`/${locale}`}
          className="inline-flex items-center gap-2 text-indigo-500 font-semibold hover:underline"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Homepage
        </Link>

      </div>
    </div>
  );
}
